import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table } from 'reactstrap';
import useTable from '../hooks/useTable';
import usePrevious from '../hooks/usePrevious';
import classNames from 'classnames';
import Footer from '../components/footer';
import ListingCard from '../components/listingCard';
import ListingModalComments from '../components/listingModalComments';
import * as activityActions from '../actions/activity';
import '../scss/_listViewTable.scss';
import { displayListingPrice } from './listingDetailsHelpers';

const ListViewTable = (props) => {
    const activities = useSelector(state => state.entities.Activities);
    const myProfile = useSelector(state => state.profile.mine);
    const dispatch = useDispatch();
    const [listing, setListing] = useState(null);
    //const [commentsBar, setCommentsBar] = useState(false);
    const commentsBar = false;
    const {
        onSortChange,
        doSort,
        sortOrder,
        sortDirection
    } = useTable()

    useEffect(() => {
        if (props.listingIds && props.listingIds.length > 0) {
            if (!listing || !props.listingIds.includes(listing.Id)) {
                // on page initialize or listing not on page, take first
                setListing(props.listingDetails[props.listingIds[0]])
            }
            else if (JSON.stringify(listing) !== JSON.stringify(props.listingDetails[listing.Id])) {
                // listing updated (e.g interestLevel)
                setListing(props.listingDetails[listing.Id])
            }
        }
        else // reset listing to null if no results
            setListing(null);

    }, [props.listingDetails, props.listingIds, listing])

    const prevListing = usePrevious(listing)
    useEffect(() => {
        const activityCommentsCount = listing && listing.ActivityId && 
            activities[listing.ActivityId] && activities[listing.ActivityId].CommentsCount;

        if (JSON.stringify(prevListing) !== JSON.stringify(listing)) {
            // switching listing and has activity id so fetch comments
            if (listing && listing.ActivityId && !activities[listing.ActivityId])
                dispatch(activityActions.fetchSingleActivity(listing.ActivityId))
        }
        // if comment added update listing
        else if (activityCommentsCount && activityCommentsCount !== listing.CommentsCount)
            setListing({...listing, CommentsCount: activityCommentsCount });

    }, [listing, activities, dispatch, prevListing])

    if (!listing || !props.listingIds) return null;

    // original list-view behavior - switch to cards/gallery on small screens 
    if (props.underLandscapeTablet)
        return <div>
            {props.listingIds.map((id, i) => {
                return <ListingCard key={i} listing={props.listingDetails[id]} {...props} />
            })}
        </div>

    const data = {
        fields: ["StreetAddress", "City", "ListPrice", "BedroomsTotal", "BathroomsFull", "LotSqft"],
        labels: ["Address", "City", "Price", "Beds", "Baths", "Sq.Ft"],
        data: props.listingIds.map(id => props.listingDetails[id])
    }

    return <div className={classNames('list-view-table', { 
            'with-comments': commentsBar, 
            'map-hidden': !props.showMap
        })}>
        <div onMouseEnter={(e) => props.onMouseEnter(listing)} 
            onMouseLeave={() => props.onMouseLeave()}>
            <ListingCard {...props}
                listing={listing} listViewTable
                commentsBarOpen={commentsBar}
                onCommentsClick={() => {
                    // turning off in-control comments to use modal, commentsBar always false. TODO remove when final 
                    //setCommentsBar(!commentsBar);
                    props.onCommentsClick(listing.Id);
                }}
                onInterestLevelChange={props.onInterestLevelChange}
                onReadMoreClick={props.onClick}
            />
            {commentsBar &&
                <div className="activity activity_overflow_auto">
                    <ListingModalComments
                        listing={listing}
                        comments={activities[listing.ActivityId] ? activities[listing.ActivityId].Comments : null}
                        myProfile={myProfile} />
                </div>
            }
        </div>
        <div className="list-view-table-container">
            <Table className="dyna-table" size="sm" striped bordered responsive>
                <thead>
                    <tr>
                        {data.labels.map((label, i) => {
                            return <th key={i}>
                                {label !== 'Address' &&
                                    <button className="btn btn-link"
                                        onClick={(e) => { e.preventDefault(); onSortChange(data.fields[i]) }}>
                                        {label}
                                        {sortOrder === data.fields[i] && sortDirection}
                                    </button>
                                }
                                {label === 'Address' && <span>Address</span>}
                            </th>
                        })}
                    </tr>
                </thead>
                <tbody className="list-view-table-tbody">
                    {data.data.length > 0 && doSort([...data.data]).map((obj, i) => {
                        return <tr key={i} className={classNames('table-row cursor-pointer', { 'selected': obj.Id === listing.Id })}
                                onClick={() => setListing(obj)}
                                onMouseEnter={(e) => props.onMouseEnter(obj)} 
                                onMouseLeave={() => props.onMouseLeave()}>
                            {data.fields.map((field, j) => {
                                if (field === 'LotSqft')
                                    return <td key={j}>
                                       <span className="square-ft">{obj[field]}</span> 
                                        <TableIndicators id={obj.Id} onCommentsClick={props.onCommentsClick} 
                                            displayInterest={obj.InterestLevel} commentsCount={obj.CommentsCount} />
                                    </td>
                                else if (field === 'ListPrice')
                                    return <td key={j}>{displayListingPrice(obj)}</td>
                                 else
                                    return <td key={j}>{obj[field]}</td>
                            })}
                        </tr>
                    })}
                    {data.data.length === 0 &&
                        <tr>
                            <td>No data found</td>
                        </tr>
                    }
                </tbody>
            </Table>
            <div className={classNames('row mg-rt-0 mg-lft-0', { 'mg-btm-47': props.showMap, 'mg-btm-50': !props.showMap })}>
                <div className="col">
                    <Footer settings={props.settings} />
                </div>
            </div>
        </div>
    </div>
}

export default ListViewTable;

const TableIndicators = props => {
    const { displayInterest, commentsCount, id, onCommentsClick } = props;
    return <div className="listing_buttons_container">
        <div className={"listing_buttons list-view"}>
            {displayInterest === 'Y' &&
                <span className={'imgicon heart selected'} />
            }
            {displayInterest === 'N' &&
                <span className={'imgicon rejected selected'} />
            }
            {displayInterest === 'M' &&
                <span className={'imgicon maybe selected'} />
            }
            {commentsCount > 0 &&
                <span className="comment_count"
                    onClick={() => onCommentsClick(id)}>
                    <span className="imgicon chat"></span> {commentsCount}
                </span>
            }
        </div>
    </div>
}